import { DailyMetrics } from '../types';

// Use server-side proxy to avoid CORS issues in production
const PROXY_BASE = '/api/oura-proxy';

// ── Oura API response types ──────────────────────────────────────────────────

interface OuraDailySleep {
  day: string;
  score: number | null;
  contributors?: { total_sleep?: number; efficiency?: number; hrv_balance?: number };
}

interface OuraSleepSession {
  day: string;
  type: string;
  bedtime_start?: string;
  bedtime_end?: string;
  total_sleep_duration?: number;
  deep_sleep_duration?: number;
  rem_sleep_duration?: number;
  light_sleep_duration?: number;
  time_in_bed?: number;
  lowest_heart_rate?: number | null;
}

interface OuraDailyReadiness {
  day: string;
  score: number | null;
  contributors?: { hrv_balance?: number };
}

interface OuraDailyActivity {
  day: string;
  steps: number | null;
  high_activity_time?: number | null;
}

interface OuraDailyHrv {
  day: string;
  average_rmssd: number | null;
}

interface OuraDailyStress {
  day: string;
  stress_high?: number | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function clamp(val: number, min = 0, max = 100): number {
  return Math.min(max, Math.max(min, Math.round(val)));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function ouraGet(path: string, token: string, userId?: string): Promise<any> {
  // Split path from query string, send both through proxy
  const [basePath, queryString] = path.split('?');
  const params = new URLSearchParams(queryString || '');
  params.set('path', basePath);
  if (userId) params.set('user_id', userId);
  const res = await fetch(`${PROXY_BASE}?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Oura API error: ${res.status}`);
  return res.json();
}

function dateRange(days: number): { start: string; end: string } {
  const end   = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  // Use local dates — toISOString() returns UTC which can be off by a day
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: fmt(start), end: fmt(end) };
}

export async function fetchOuraMetrics(token: string, days = 30, userId?: string): Promise<DailyMetrics[]> {
  const { start, end } = dateRange(days);
  const params = `?start_date=${start}&end_date=${end}`;

  const [sleepData, readinessData, activityData, hrvData, stressData, sleepSessionData] = await Promise.allSettled([
    ouraGet(`/usercollection/daily_sleep${params}`, token, userId),
    ouraGet(`/usercollection/daily_readiness${params}`, token, userId),
    ouraGet(`/usercollection/daily_activity${params}`, token, userId),
    ouraGet(`/usercollection/daily_hrv${params}`, token, userId),
    ouraGet(`/usercollection/daily_stress${params}`, token, userId),
    ouraGet(`/usercollection/sleep${params}`, token, userId),
  ]);

  const sleepMap:     Record<string, OuraDailySleep> = {};
  const readinessMap: Record<string, OuraDailyReadiness> = {};
  const activityMap:  Record<string, OuraDailyActivity> = {};
  const hrvMap:       Record<string, number> = {};
  const stressMap:    Record<string, OuraDailyStress> = {};
  const bedtimeMap:   Record<string, string> = {};  // day → bedtime_start ISO string
  const sleepStageMap: Record<string, { deep: number; rem: number; light: number; lowestHR: number | null; totalMins: number }> = {};

  if (sleepData.status === 'fulfilled') {
    (sleepData.value?.data || []).forEach((d: OuraDailySleep) => { sleepMap[d.day] = d; });
  }
  // Sleep sessions: pick longest session per night for bedtime_start + sleep stages
  if (sleepSessionData.status === 'fulfilled') {
    const sessions: OuraSleepSession[] = sleepSessionData.value?.data || [];
    const byDay: Record<string, OuraSleepSession[]> = {};
    sessions.forEach((s: OuraSleepSession) => {
      if (s.type !== 'long_sleep') return;
      const day = s.day;
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(s);
    });
    Object.entries(byDay).forEach(([day, daySessions]) => {
      const longest = daySessions.sort((a, b) =>
        (b.total_sleep_duration || 0) - (a.total_sleep_duration || 0)
      )[0];
      if (longest?.bedtime_start) bedtimeMap[day] = longest.bedtime_start;
      // Extract sleep stages, lowest HR, and total sleep from longest session
      if (longest) {
        const deep  = longest.deep_sleep_duration  || 0;
        const rem   = longest.rem_sleep_duration   || 0;
        const light = longest.light_sleep_duration || 0;
        // total_sleep_duration only counts staged sleep (deep+REM+light).
        // time_in_bed is full bedtime_start→bedtime_end duration.
        // For "hours slept", use time_in_bed if available, else compute from timestamps,
        // else sum stages as fallback.
        let totalSecs = longest.time_in_bed || 0;
        if (!totalSecs && longest.bedtime_start && longest.bedtime_end) {
          totalSecs = (new Date(longest.bedtime_end).getTime() - new Date(longest.bedtime_start).getTime()) / 1000;
        }
        if (!totalSecs) {
          totalSecs = deep + rem + light;
        }
        sleepStageMap[day] = {
          deep:      Math.round(deep / 60),
          rem:       Math.round(rem / 60),
          light:     Math.round(light / 60),
          lowestHR:  longest.lowest_heart_rate ?? null,
          totalMins: Math.round(totalSecs / 60),
        };
      }
    });
  }
  if (readinessData.status === 'fulfilled') {
    (readinessData.value?.data || []).forEach((d: OuraDailyReadiness) => { readinessMap[d.day] = d; });
  }
  if (activityData.status === 'fulfilled') {
    (activityData.value?.data || []).forEach((d: OuraDailyActivity) => { activityMap[d.day] = d; });
  }
  if (hrvData.status === 'fulfilled') {
    // daily_hrv returns { day, average_rmssd, ... }
    (hrvData.value?.data || []).forEach((d: OuraDailyHrv) => {
      if (d.average_rmssd != null) hrvMap[d.day] = Math.round(d.average_rmssd);
    });
  }
  if (stressData.status === 'fulfilled') {
    (stressData.value?.data || []).forEach((d: OuraDailyStress) => { stressMap[d.day] = d; });
  }

  const allDays = new Set([
    ...Object.keys(sleepMap),
    ...Object.keys(readinessMap),
    ...Object.keys(activityMap),
  ]);

  const metrics: DailyMetrics[] = [];

  allDays.forEach(date => {
    const sleep    = sleepMap[date];
    const ready    = readinessMap[date];
    const activity = activityMap[date];
    const stress   = stressMap[date];
    // Use daily_hrv average_rmssd, fall back to readiness hrv_balance contributor
    const hrv = hrvMap[date] != null
      ? hrvMap[date]
      : ready?.contributors?.hrv_balance != null
        ? Math.round(ready.contributors.hrv_balance * 1.2) // scale 0-100 contributor to approx ms
        : null;

    // Stress: stress_high is in seconds (max ~14400s = 4hrs)
    // Invert so high stress_high = low score
    const maxStressSeconds = 14400;
    const stressVal = stress?.stress_high != null
      ? clamp(100 - Math.round((stress.stress_high / maxStressSeconds) * 100))
      : null;

    const stages = sleepStageMap[date];

    // Total sleep: prefer sleep session total_sleep_duration (actual tracked sleep)
    // Fallback: daily_sleep contributors score → approx hours
    const totalSleepMins = stages?.totalMins && stages.totalMins > 0
      ? stages.totalMins
      : sleep?.contributors?.total_sleep != null
      ? Math.round((sleep.contributors.total_sleep / 100) * 8 * 60)
      : null;

    metrics.push({
      date,
      sleep_score:      sleep?.score    ?? null,
      hrv,
      readiness:        ready?.score    ?? null,
      steps:            activity?.steps ?? null,
      exercise_mins:    activity?.high_activity_time != null
        ? Math.round(activity.high_activity_time / 60) : null,
      stress_score:     stressVal,
      deep_sleep_mins:  stages?.deep  || null,
      rem_sleep_mins:   stages?.rem   || null,
      light_sleep_mins: stages?.light || null,
      total_sleep_mins: totalSleepMins,
      sleep_efficiency: sleep?.contributors?.efficiency ?? null,
      lowest_hr:        stages?.lowestHR ?? null,
      bedtime_start:    bedtimeMap[date] ?? null,
    });
  });

  return metrics.sort((a, b) => a.date.localeCompare(b.date));
}

export function computeScores(metrics: DailyMetrics[]): {
  sleep: number; hrv: number; readiness: number; movement: number; stress: number; overall: number;
} {
  if (!metrics.length) return { sleep: 0, hrv: 0, readiness: 0, movement: 0, stress: 0, overall: 0 };

  // Use last 7 days with actual data
  const recent = metrics.slice(-7);

  function avg(arr: (number | null)[]): number {
    const vals = arr.filter((v): v is number => v !== null && v > 0);
    if (!vals.length) return 0;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }

  const sleep     = avg(recent.map(d => d.sleep_score));
  const readiness = avg(recent.map(d => d.readiness));
  const stress    = avg(recent.map(d => d.stress_score));

  // HRV: normalize rmssd ms value to 0-100 using typical range 20–120ms
  // A 45ms HRV → score ~25, 80ms → ~60, 120ms → 100
  const hrv = avg(recent.map(d => {
    if (!d.hrv || d.hrv < 1) return null;
    return clamp(Math.round(((d.hrv - 15) / 105) * 100));
  }));

  // Movement: sqrt(steps/10000)*50 + min(exercise_mins/30,1)*50
  // Square root smoothing on steps so early steps count more; exercise capped at 30 min
  const movement = avg(recent.map(d => {
    if (d.steps == null && d.exercise_mins == null) return null;
    const stepsPart = d.steps != null ? Math.sqrt(Math.min(1, d.steps / 10000)) * 50 : 0;
    const exPart    = d.exercise_mins != null ? Math.min(1, d.exercise_mins / 30) * 50 : 0;
    return clamp(Math.round(stepsPart + exPart));
  }));

  const overall = avg([sleep, hrv, readiness, movement, stress].filter(v => v > 0));

  return { sleep, hrv, readiness, movement, stress, overall };
}
